<div class="hm-market">
  <div class="hm-sec-head">
    <p class="hm-eyebrow">
      {{ member ? 'Your market' : 'This week in the market' }}
    </p>
    <div class="hm-sec-title-row">
      <h2 class="hm-h-sec">
        {{
          member
            ? 'Your plates, and the market behind them'
            : 'The Private Registration Index'
        }}
      </h2>
    </div>
  </div>

  <!-- Auction calendar strip (LIVE: /auction/fixture) — an editorial line,
       not a dashboard widget. Only when a real fixture exists. -->
  <a
    class="hm-aux-fixture"
    *ngIf="member && fixture?.hasAuction && fixtureLine()"
    routerLink="/account/updates"
  >
    <span class="hm-aux-label">Auction calendar</span>
    <span class="hm-aux-main">{{ fixtureLine() }}</span>
    <span
      class="hm-aux-match"
      *ngIf="fixture?.matchingLotCount != null && (fixture?.matchingLotCount || 0) > 0"
      >{{ fixture?.matchingLotCount }} match{{ fixture?.matchingLotCount === 1 ? '' : 'es' }} your interests</span
    >
    <span class="hm-aux-go">All fixtures <span class="arw" aria-hidden="true">→</span></span>
  </a>

  <!-- Member standing rows (LIVE): tracked plates, saved plates, then
       recently viewed; each plate appears once, in its first group. -->
  <div class="hm-standing" *ngIf="member && standingRows.length > 0">
    <ng-container *ngIf="followedRows.length > 0">
      <div class="hm-standing-head">
        <span class="hm-kicker">Your tracked plates</span>
        <a class="hm-more" routerLink="/account/followed"
          >All tracked plates <span class="arw" aria-hidden="true">→</span></a
        >
      </div>
      <ng-container *ngFor="let row of followedRows">
        <ng-container *ngTemplateOutlet="standingRow; context: { $implicit: row }"></ng-container>
      </ng-container>
    </ng-container>
    <ng-container *ngIf="savedOnlyRows.length > 0">
      <div class="hm-standing-head">
        <span class="hm-kicker">Your saved plates</span>
        <a class="hm-more" routerLink="/account/saved"
          >All saved plates <span class="arw" aria-hidden="true">→</span></a
        >
      </div>
      <ng-container *ngFor="let row of savedOnlyRows">
        <ng-container *ngTemplateOutlet="standingRow; context: { $implicit: row }"></ng-container>
      </ng-container>
    </ng-container>
    <ng-container *ngIf="recentOnlyRows.length > 0">
      <div class="hm-standing-head">
        <span class="hm-kicker">Recently viewed</span>
        <a class="hm-more" routerLink="/account/recent"
          >All recently viewed <span class="arw" aria-hidden="true">→</span></a
        >
      </div>
      <ng-container *ngFor="let row of recentOnlyRows">
        <ng-container *ngTemplateOutlet="standingRow; context: { $implicit: row }"></ng-container>
      </ng-container>
    </ng-container>
  </div>

  <ng-template #standingRow let-row>
    <a
      class="hm-standing-row"
      [routerLink]="plateUrl(row.registration)"
      [attr.aria-label]="
        row.registration +
        ' · ' +
        row.statusLine +
        ' · ' +
        row.contextLine
      "
    >
      <pla-plate class="hm-plate-slot" [rawPlate]="row.formattedRegistration || row.registration" [showPrice]="false"
        [openModal]="false" [clickThrough]="false" [center]="false" [mobileSmall]="true"
        [customClass]="'flat shaded'" [plateSize]="'ns-s'"></pla-plate>
      <span class="hm-standing-kind">{{ row.kind }}</span>
      <span class="hm-standing-status">{{ row.statusLine }}</span>
      <span class="hm-standing-context">{{ row.contextLine }}</span>
      <span class="hm-standing-cta">View plate</span>
    </a>
  </ng-template>

  <div class="hm-market-grid">
    <!-- Index panel (STATIC: platex-index deliverables; provenance in service) -->
    <div class="hm-index-tile">
      <div class="hm-index-top">
        <div>
          <p class="hm-index-value">
            {{ index.value }} <span class="hm-index-unit">index</span>
          </p>
          <p class="hm-index-base">{{ index.baseLabel }}</p>
        </div>
        <div class="hm-index-change">
          <span class="hm-index-yoy" [class.is-down]="index.direction === 'down'"
            [class.is-flat]="index.direction === 'flat'">{{ index.direction === 'up' ? '▲' : index.direction === 'down' ? '▼' : '–' }}
            {{ index.yoyLabel }}</span>
          <span class="hm-index-context">{{ index.contextLabel }}</span>
        </div>
      </div>
      <svg class="hm-index-chart" [attr.viewBox]="chart.viewBox"
        preserveAspectRatio="none" aria-hidden="true">
        <defs>
          <linearGradient id="hmIndexFill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stop-color="#0e2b33" stop-opacity="0.12" />
            <stop offset="1" stop-color="#0e2b33" stop-opacity="0" />
          </linearGradient>
        </defs>
        <line class="hm-ic-grid" *ngFor="let g of chart.grid" x1="0" x2="854"
          [attr.y1]="g.y" [attr.y2]="g.y" />
        <text class="hm-ic-axis" *ngFor="let g of chart.grid" x="862"
          [attr.y]="g.y + 4">{{ g.label }}</text>
        <path class="hm-ic-area" [attr.d]="chart.area" />
        <path class="hm-ic-line" [attr.d]="chart.line" />
        <path *ngIf="chart.partialLine" class="hm-ic-line hm-ic-partial" [attr.d]="chart.partialLine" />
        <circle class="hm-ic-dot" [attr.cx]="chart.dotX" [attr.cy]="chart.dotY" r="4" />
        <text class="hm-ic-axis" *ngFor="let l of chart.xLabels" [attr.x]="l.x"
          y="246" [attr.text-anchor]="l.anchor">{{ l.label }}</text>
      </svg>
      <p class="hm-index-src">{{ index.sourceLabel }} · {{ index.asOf }}</p>
    </div>

    <!-- Verified sales (LIVE: MarketWatch yearly-top-sold) -->
    <div class="hm-sales">
      <div class="hm-standing-head">
        <span class="hm-kicker">Verified · largest recent sales</span>
      </div>
      <ng-container *ngIf="sales != null; else salesLoading">
        <ng-container *ngIf="sales.length > 0; else salesEmpty">
          <a
            class="hm-sale-row"
            *ngFor="let sale of sales"
            [routerLink]="plateUrl(sale.registration)"
          >
            <pla-plate class="hm-plate-slot" [rawPlate]="sale.formattedRegistration || sale.registration"
              [showPrice]="false" [openModal]="false" [clickThrough]="false" [center]="false"
              [mobileSmall]="true" [customClass]="'flat shaded'" [plateSize]="'ns-s'"></pla-plate>
            <span class="hm-sale-meta"
              >{{ sale.seller }}<ng-container *ngIf="formatDate(sale)">
                · {{ formatDate(sale) }}</ng-container
              ></span
            >
            <span class="hm-sale-price">{{ formatPrice(sale) }}</span>
          </a>
          <p class="hm-sale-note">
            Auction figures are shown as recorded; where a result is a hammer
            price, fees are not included.
          </p>
        </ng-container>
      </ng-container>
      <ng-template #salesLoading>
        <p class="hm-sale-note">Loading verified sales…</p>
      </ng-template>
      <ng-template #salesEmpty>
        <p class="hm-sale-note">
          {{
            salesFailed
              ? 'Verified sales are unavailable right now.'
              : 'No verified sales recorded yet this year.'
          }}
        </p>
      </ng-template>
    </div>
  </div>
</div>
